// UserForm component
import React, {useContext, useState, useMemo, useEffect} from "react";
import UseHybridForm from "../../common/forms/Hooks/UseHybridForm";
import InputValidatorField from "../../common/components/Inputs/InputValidatorField";
import {IsValidEmail, IsValidText} from "../../../utils/Validators";
import IconButton from "@mui/material/IconButton";
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { PencilSimple } from '@phosphor-icons/react';
import {addTemplateParticipant, inviteTemplateParticipant, removeTemplateParticipant} from "../../../services/template/Participants";
import {getGroupParticipantsExcludingBill, getGroupParticipants} from "../../../services/group/Groups";
import {AuthContext} from "../../auth/providers/AuthProvider";
import {CsrfContext} from "../../../providers/CsrfProvider";
import {TemplateContext} from "../../../providers/TemplateProvider";
import {Typography, Box, Autocomplete, TextField, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Popover, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Snackbar, Alert, CircularProgress} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FormatNumber from '../../../utils/formatters/FormatNumber';
import {ThemeContext} from "styled-components";
import {PaperPlaneTilt, User, UserPlus} from "@phosphor-icons/react";
import {useQuery, useQueryClient} from "react-query";
import {getUsersTemplate} from "../../../services/template/Templates";
import {calculateTestSplits, getBillRules} from "../../../services/bill/BillRules";
import {getTracks, updateTrack, deleteTrack} from "../../../services/bill/BillingTracks";
import AddParticipantRow from "../components/AddParticipantRow";
import TableWithActions from "../../common/components/Wrappers/TableWithActions";

// Derive participants (users) from tracks; same shape as listTemplateParticipants result. Single GET source: templates/{id}/participants.
function usersFromTracks(tracks) {
    if (!tracks || !Array.isArray(tracks)) return [];
    return tracks.filter(t => t.user_id && t.user).map(t => t.user);
}

const ParticipantItem = ({templateId, user, onRemove, estimatedCost, typicalCost, calculatedSplits, tracks, groupId, groupMembers, enhancedGroupMembers, loadingGroupMembers, onEditParticipant, editingUserId, setEditingUserId, isAddingUser, setIsAddingUser, onShowNotification, onDropdownOpen}) => {
    const {csrfToken} = useContext(CsrfContext)
    const { authClaims } = useContext(AuthContext);
    const { ownerSub } = useContext(TemplateContext);
    const queryClient = useQueryClient();
    const [inviteSent, setInviteSent] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [editEmail, setEditEmail] = useState(user?.email || '');
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const theme = useContext(ThemeContext);
    
    const isEditing = editingUserId === user.id;

    const handleCostClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleMenuClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const menuOpen = Boolean(menuAnchorEl);

    // Get breakdown for this user from calculated splits
    const userBreakdown = useMemo(() => {
        if (!calculatedSplits || !calculatedSplits.rule_breakdowns || !user?.id) return [];

        const breakdown = [];
        calculatedSplits.rule_breakdowns.forEach(ruleBreakdown => {
            const participantSplit = ruleBreakdown.participants?.find(p => p.user_id === user.id);
            if (participantSplit && participantSplit.amount > 0) {
                breakdown.push({
                    ruleType: ruleBreakdown.rule_type,
                    amount: participantSplit.amount,
                    ruleAmount: ruleBreakdown.total_applied,
                });
            }
        });
        return breakdown;
    }, [calculatedSplits, user?.id]);

    const handleSendInvite = async () => {
        // Example API call to send invite
        const formData = {
            templateId: templateId, // Example template ID
            email: user.email // Participant's email
        };

// Now, call the function
        const invite = inviteTemplateParticipant(csrfToken);

// Use the function
        const executeInvite = async () => {
            try {
                const result = await invite(formData);
                setInviteSent(true);
            } catch (error) {
                console.error('Failed to invite participant:', error);
            }
        };

    // Execute the function at the appropriate place in your code
        await executeInvite();
    };

    const handleStartEdit = () => {
        handleMenuClose();
        setIsAddingUser(false); // Close add form if open
        setEditingUserId(user.id);
        setEditEmail(user.email);
    };

    const handleCancelEdit = () => {
        setEditingUserId(null);
        setEditEmail('');
    };

    // Find the track for this user
    const userTrack = useMemo(() => {
        if (!tracks || !user?.id) return null;
        return tracks.find(t => t.user_id === user.id);
    }, [tracks, user?.id]);

    const handleChangeUser = async (e) => {
        e.preventDefault();
        if (!editEmail || !IsValidEmail(editEmail.trim())) {
            alert('Please enter a valid email address');
            return;
        }

        if (editEmail.trim().toLowerCase() === user.email.toLowerCase()) {
            handleCancelEdit();
            return;
        }

        try {
            const currentTracks = await queryClient.fetchQuery(
                ['billingTracks', String(templateId)],
                () => getTracks(String(templateId), csrfToken)
            );
            const currentParticipants = usersFromTracks(currentTracks);

            const existingParticipant = currentParticipants?.find(p =>
                p.email?.toLowerCase().trim() === editEmail.trim().toLowerCase()
            );

            let newParticipantId = null;

            if (existingParticipant && existingParticipant.id) {
                // New email already exists as a participant
                newParticipantId = existingParticipant.id;
                
                // Find the track for the existing participant (if any)
                const existingTrack = currentTracks?.find(t => t.user_id === existingParticipant.id);
                
                // If the existing participant has a different track, delete it
                // (we're going to swap the current user's track to them)
                if (existingTrack && existingTrack.id !== userTrack?.id) {
                    await deleteTrack(String(templateId), existingTrack.id, csrfToken);
                }
                
                // Update the current user's track to point to the new participant
                // This is the swap - the track stays the same, just the user_id changes
                if (userTrack) {
                    await updateTrack(String(templateId), userTrack.id, newParticipantId, csrfToken);
                }
            } else {
                // New email doesn't exist - add the new participant first
                const mutation = addTemplateParticipant(csrfToken);
                await mutation({ email: editEmail.trim(), templateId: templateId });
                
                const updatedTracksForEdit = await queryClient.fetchQuery(
                    ['billingTracks', String(templateId)],
                    () => getTracks(String(templateId), csrfToken),
                    { staleTime: 0 }
                );
                const updatedParticipants = usersFromTracks(updatedTracksForEdit);

                const newParticipant = updatedParticipants?.find(p =>
                    p.email?.toLowerCase().trim() === editEmail.trim().toLowerCase()
                );

                if (!newParticipant || !newParticipant.id) {
                    throw new Error('Failed to find newly added participant');
                }
                
                newParticipantId = newParticipant.id;
                
                const newParticipantTrack = updatedTracksForEdit?.find(t => t.user_id === newParticipantId);
                
                // If backend created a new track for the new participant, delete it
                // and update the current user's track to point to the new participant instead
                if (newParticipantTrack && newParticipantTrack.id !== userTrack?.id) {
                    // Delete the newly created track
                    await deleteTrack(String(templateId), newParticipantTrack.id, csrfToken);
                }
                
                // Update the current user's track to point to the new participant
                // This swaps the track from old user to new user
                if (userTrack) {
                    await updateTrack(String(templateId), userTrack.id, newParticipantId, csrfToken);
                }
            }
            
            // Now remove the old participant
            // Since the track is already pointing to the new user, this won't create a vacancy
            const remove = removeTemplateParticipant(csrfToken);
            await remove({ templateId: templateId, email: user.email });
            
            await Promise.all([
                queryClient.refetchQueries(['billingTracks', String(templateId)]),
                queryClient.refetchQueries(['billRules', String(templateId)])
            ]);
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);

            if (onEditParticipant) {
                onEditParticipant();
            }
            handleCancelEdit();
        } catch (error) {
            console.error('❌ Error changing user:', error);
            alert('Error changing user: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleRemoveUser = async () => {
        handleMenuClose();
        
        try {
            // Fetch rules to check if this track is referenced in non-default rules
            const rules = await getBillRules(String(templateId), csrfToken);
            
            // Check if this track is referenced in any non-default rules
            const referencedInNonDefaultRules = rules.filter(rule => {
                if (rule.is_default) return false;
                
                // Check if any participant in the rule references this track
                return rule.participants?.some(participant => {
                    return participant.track_id === userTrack?.id;
                });
            });
            
            if (referencedInNonDefaultRules.length > 0) {
                // Show notification that user is in other rules (at parent level so it persists)
                if (onShowNotification) {
                    onShowNotification({
                        open: true,
                        message: `${user.email} is currently assigned to other rules. A vacancy will be left to be filled later.`,
                        severity: 'info'
                    });
                }
                
                setRemoving(true);
                const remove = removeTemplateParticipant(csrfToken);
                await remove({ templateId: templateId, email: user.email });

                if (userTrack) {
                    // Leave vacancy by setting user_id to null
                    await updateTrack(String(templateId), userTrack.id, null, csrfToken);
                }
                
                // Delay refetch to allow notification to display
                setTimeout(async () => {
                    await queryClient.refetchQueries(['billingTracks', String(templateId)]);
                    await queryClient.refetchQueries(['billRules', String(templateId)]);
                    queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);

                    if (onRemove) {
                        onRemove();
                    }
                    handleCancelEdit();
                    setRemoving(false);
                }, 500);
            } else {
                // User is only in default rule - confirm deletion
                if (!window.confirm(`Are you sure you want to remove ${user.email} from this bill?`)) {
                    return;
                }
                
                setRemoving(true);
                const remove = removeTemplateParticipant(csrfToken);
                await remove({ templateId: templateId, email: user.email });

                if (userTrack) {
                    // Delete the track entirely since user is not in any other rules
                    await deleteTrack(String(templateId), userTrack.id, csrfToken);
                }

                await queryClient.refetchQueries(['billingTracks', String(templateId)]);
                await queryClient.refetchQueries(['billRules', String(templateId)]);
                queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);

                if (onRemove) {
                    onRemove();
                }
                handleCancelEdit();
                setRemoving(false);
            }
        } catch (error) {
            setRemoving(false);
            alert('Error removing participant: ' + (error.response?.data?.error || error.message));
        }
    };

    const isOwner = authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();
    const hasChanged = editEmail.trim() !== user.email;
    const isValid = editEmail.trim() !== '' && IsValidEmail(editEmail.trim());

    return (
        <>
            {/* Header Row (when editing) */}
            {isEditing && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Edit Participant
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleCancelEdit}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
            
            {/* Form Row (when editing) */}
            {isEditing && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 2 }}>
                        <Box component="form" onSubmit={handleChangeUser} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Autocomplete
                                freeSolo
                                open={autocompleteOpen}
                                onOpen={() => { setAutocompleteOpen(true); onDropdownOpen?.(); }}
                                onClose={() => setAutocompleteOpen(false)}
                                options={groupId && !loadingGroupMembers ? (enhancedGroupMembers || groupMembers || []) : []}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.email || '';
                                }}
                                inputValue={editEmail}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input' || reason === 'clear') {
                                        setEditEmail(newInputValue);
                                    }
                                }}
                                onChange={(event, value) => {
                                    if (value) {
                                        const emailValue = typeof value === 'string' ? value : value.email;
                                        if (emailValue) {
                                            setEditEmail(emailValue);
                                        }
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = options.filter((option) => {
                                        const emailValue = typeof option === 'string' ? option : option.email;
                                        return emailValue.toLowerCase().includes(params.inputValue.toLowerCase());
                                    });
                                    return filtered;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Email"
                                        placeholder="Enter email or select from group"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.colors.text,
                                                fontSize: '16px',
                                                '& fieldset': {
                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.colors.secondary || theme.colors.brandSecondary,
                                                fontSize: '16px',
                                                '&.Mui-focused': {
                                                    color: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '16px',
                                            },
                                        }}
                                    />
                                )}
                                loading={loadingGroupMembers}
                            />
                            
                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                {(!hasChanged || !isValid) ? (
                                    <Button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.text,
                                            '&:hover': {
                                                borderColor: theme.colors.border || '#ccc',
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                            '&:hover': {
                                                borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        Save
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            )}
            
            {/* Normal Row (when not editing) */}
            {!isEditing && (
            <TableRow>
                <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                <TableCell sx={{
                    color: theme.colors.text,
                    pl: 0,
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 0,
                }}>
                    {user.email}
                </TableCell>
                <TableCell align="right" sx={{ 
                    color: theme.colors.text, 
                    fontWeight: 500, 
                    padding: { xs: '1rem 0.125rem', sm: '1rem 0' },
                    fontSize: { xs: '0.9rem', sm: '1.1rem' },
                    width: { xs: '3.125rem', sm: '6.25rem' },
                    minWidth: { xs: '3.125rem', sm: '6.25rem' },
                    maxWidth: { xs: '3.125rem', sm: '6.25rem' },
                    whiteSpace: 'nowrap',
                }}>
                    {estimatedCost !== null && estimatedCost !== undefined ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, marginRight: 0 }}>
                            <Tooltip title="Click to see cost breakdown">
                                <HelpOutlineIcon
                                    sx={{
                                        color: theme.colors.text,
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        opacity: 0.6,
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleCostClick}
                                />
                            </Tooltip>
                            <Typography
                                sx={{
                                    color: theme.colors.text,
                                    fontWeight: 500,
                                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    }
                                }}
                                onClick={handleCostClick}
                            >
                                ${FormatNumber(estimatedCost)}
                            </Typography>
                        </Box>
                    ) : '-'}
                </TableCell>
                <TableCell sx={{ 
                    width: '3.5rem !important', 
                    minWidth: '3.5rem !important', 
                    maxWidth: '3.5rem !important', 
                    p: 0,
                    flexShrink: 0,
                    flexGrow: 0,
                }}>
                    {isOwner && !isEditing && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleMenuClick}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '8px',
                                    minWidth: '40px',
                                    minHeight: '40px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                </Box>
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={menuOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={handleStartEdit}>Edit</MenuItem>
                                <MenuItem onClick={handleRemoveUser} disabled={removing}>
                                    {removing ? 'Removing...' : 'Remove'}
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
            )}
            
            {/* Cost Breakdown Popover */}

            {/* Cost Breakdown Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 2, maxWidth: 400, backgroundColor: theme.colors.background }}>
                    <Typography variant="h6" sx={{ color: theme.colors.text, mb: 1 }}>
                        Cost Breakdown for {user.email}
                    </Typography>
                    {typicalCost > 0 && (
                        <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.7, mb: 2 }}>
                            Based on typical cost: ${FormatNumber(typicalCost)}
                        </Typography>
                    )}
                    {userBreakdown.length > 0 ? (
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: theme.colors.text, mb: 1, fontWeight: 600 }}>
                                Rule Contributions:
                            </Typography>
                            {userBreakdown.map((item, index) => (
                                <Box key={index} sx={{ mb: 1, pl: 1, borderLeft: `2px solid ${theme.colors.primary || '#1976d2'}` }}>
                                    <Typography variant="body2" sx={{ color: theme.colors.text }}>
                                        {item.ruleType === 'minus' ? 'Minus Rule' :
                                         item.ruleType === 'percent' ? 'Percent Rule' :
                                         'Evenly Rule'}: ${FormatNumber(item.amount)}
                                    </Typography>
                                </Box>
                            ))}
                            <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${theme.colors.borderContrast || '#ccc'}` }}>
                                <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                    Total: ${FormatNumber(estimatedCost)}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.7 }}>
                            No rules currently apply to this participant, or cost is split evenly among all participants.
                        </Typography>
                    )}
                </Box>
            </Popover>
        </>
    );
}

const VacancyRow = ({ track, templateId, isOwner, onFillVacancy, groupId, groupMembers, enhancedGroupMembers, loadingGroupMembers, theme, csrfToken, queryClient, editingUserId, setEditingUserId, isAddingUser, setIsAddingUser, onShowNotification, onDropdownOpen }) => {
    const [fillEmail, setFillEmail] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const isEditingVacancy = editingUserId === track.id;

    const handleMenuClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleStartFill = () => {
        handleMenuClose();
        setIsAddingUser(false); // Close add form if open
        setEditingUserId(track.id);
        setFillEmail('');
    };

    const menuOpen = Boolean(menuAnchorEl);

    const handleCancelEdit = () => {
        setEditingUserId(null);
        setFillEmail('');
    };

    const handleFillSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        if (fillEmail.trim() && IsValidEmail(fillEmail.trim())) {
            await onFillVacancy(track.id, fillEmail.trim(), false);
            setFillEmail('');
            handleCancelEdit();
        }
    };

    const handleDeleteVacancy = async () => {
        handleMenuClose();
        if (deleting) return;
        
        try {
            // Fetch rules to check if this track is referenced
            const rules = await getBillRules(String(templateId), csrfToken);
            
            // Check if this track ID is referenced in any non-default rules
            const referencedInNonDefaultRules = rules.filter(rule => {
                if (rule.is_default) return false;
                
                // Check if any participant in the rule references this track
                return rule.participants?.some(participant => {
                    return participant.track_id === track.id;
                });
            });
            
            if (referencedInNonDefaultRules.length > 0) {
                if (onShowNotification) {
                    onShowNotification({
                        open: true,
                        message: 'This vacancy cannot be deleted because it is referenced in one or more custom rules. Please remove the vacancy from all rules first.',
                        severity: 'warning'
                    });
                }
                return;
            }
            
            // Confirm deletion
            if (!window.confirm('Are you sure you want to delete this vacancy?')) {
                return;
            }
            
            setDeleting(true);
            await deleteTrack(String(templateId), track.id, csrfToken);
            
            // Invalidate queries to refresh data
            await queryClient.invalidateQueries(['billingTracks', String(templateId)]);
            await queryClient.invalidateQueries(['billRules', String(templateId)]);
            await queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
            
            handleCancelEdit();
        } catch (error) {
            console.error('Error deleting vacancy:', error);
            alert('Error deleting vacancy: ' + (error.response?.data?.error || error.message));
        } finally {
            setDeleting(false);
        }
    };

    const isValidEmail = fillEmail.trim() !== '' && IsValidEmail(fillEmail.trim());

    return (
        <>
            {/* Header Row (when editing) */}
            {isEditingVacancy && (
                <TableRow sx={{
                    backgroundColor: theme.colors.warning ? `${theme.colors.warning}15` : 'rgba(255, 152, 0, 0.1)',
                    '&:hover': {
                        backgroundColor: theme.colors.warning ? `${theme.colors.warning}25` : 'rgba(255, 152, 0, 0.2)',
                    }
                }}>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Manage Vacancy
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleCancelEdit}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
            
            {/* Form Row (when editing) */}
            {isEditingVacancy && (
                <TableRow>
                    <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: { xs: '0.125rem', sm: 0 }, py: 2 }}>
                        <Box component="form" onSubmit={handleFillSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Autocomplete
                                freeSolo
                                open={autocompleteOpen}
                                onOpen={() => { setAutocompleteOpen(true); onDropdownOpen?.(); }}
                                onClose={() => setAutocompleteOpen(false)}
                                options={groupId && !loadingGroupMembers ? (enhancedGroupMembers || groupMembers || []) : []}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.email || '';
                                }}
                                inputValue={fillEmail}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input' || reason === 'clear') {
                                        setFillEmail(newInputValue);
                                    }
                                }}
                                onChange={(event, value) => {
                                    if (value) {
                                        const emailValue = typeof value === 'string' ? value : value.email;
                                        if (emailValue && IsValidEmail(emailValue.trim())) {
                                            onFillVacancy(track.id, emailValue.trim(), false);
                                            setFillEmail('');
                                            handleCancelEdit();
                                        }
                                    }
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = options.filter((option) => {
                                        const emailValue = typeof option === 'string' ? option : option.email;
                                        return emailValue.toLowerCase().includes(params.inputValue.toLowerCase());
                                    });
                                    return filtered;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={groupId && enhancedGroupMembers && enhancedGroupMembers.length > 0 ? "Search group members or type email" : "Enter email"}
                                        placeholder="Type to search or enter any email"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const email = fillEmail.trim();
                                                if (email && IsValidEmail(email)) {
                                                    handleFillSubmit(e);
                                                }
                                            }
                                            if (e.key === 'Escape') {
                                                handleCancelEdit();
                                            }
                                        }}
                                        autoFocus
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.colors.text,
                                                fontSize: '16px',
                                                '& fieldset': {
                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.colors.secondary || theme.colors.brandSecondary,
                                                fontSize: '16px',
                                                '&.Mui-focused': {
                                                    color: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '16px',
                                            },
                                        }}
                                    />
                                )}
                                loading={loadingGroupMembers}
                            />
                            
                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                {!isValidEmail ? (
                                    <Button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.text,
                                            '&:hover': {
                                                borderColor: theme.colors.border || '#ccc',
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                            '&:hover': {
                                                borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        Fill
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '3.5rem !important', 
                        minWidth: '3.5rem !important', 
                        maxWidth: '3.5rem !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            )}
            
            {/* Normal Row (when not editing) */}
            {!isEditingVacancy && (
            <TableRow sx={{
                backgroundColor: theme.colors.warning ? `${theme.colors.warning}15` : 'rgba(255, 152, 0, 0.1)',
                '&:hover': {
                    backgroundColor: theme.colors.warning ? `${theme.colors.warning}25` : 'rgba(255, 152, 0, 0.2)',
                }
            }}>
                <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0 }}></TableCell>
                <TableCell sx={{ color: theme.colors.text, fontStyle: 'italic', pl: 0, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: theme.colors.text, fontStyle: 'italic' }}>
                            [Vacancy - Track #{track.track_index}]
                        </Typography>
                        <Chip
                            label="Vacant"
                            size="small"
                            sx={{
                                backgroundColor: theme.colors.warning || '#ff9800',
                                color: 'white',
                            }}
                        />
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: theme.colors.text, opacity: 0.7, fontStyle: 'italic', padding: { xs: '1rem 0.125rem', sm: '1rem 0' } }}>
                    -
                </TableCell>
                <TableCell sx={{ 
                    width: '3.5rem !important', 
                    minWidth: '3.5rem !important', 
                    maxWidth: '3.5rem !important', 
                    p: 0,
                    flexShrink: 0,
                    flexGrow: 0,
                }}>
                    {isOwner && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleMenuClick}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '8px',
                                    minWidth: '40px',
                                    minHeight: '40px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                </Box>
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={menuOpen}
                                onClose={handleMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem onClick={handleStartFill}>Fill</MenuItem>
                                <MenuItem onClick={handleDeleteVacancy} disabled={deleting}>
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
            )}
        </>
    );
};

const FormParticipants = ({ onSubmit, participants, templateId, onParticipantRemoved, groupId, hydrating = false, tracks: tracksProp, expandedView = false, headerAction, ...props }) => {
    const { ownerSub, participantsFetchEnabled } = useContext(TemplateContext);
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();
    const theme = useContext(ThemeContext);
    const [editingUserId, setEditingUserId] = useState(null);
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

    // Fetch bill data to get typical_cost
    const { data: billData } = useQuery(
        ['templates', String(templateId)],
        () => getUsersTemplate(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none',
            refetchOnWindowFocus: false,
        }
    );

    const typicalCost = billData?.typical_cost || 0;

    // Fetch tracks only when not provided by parent (e.g. ViewTemplates passes tracks from ParticipantsModel → single GET)
    const { data: tracksFromQuery } = useQuery(
        ['billingTracks', String(templateId)],
        () => getTracks(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && (participantsFetchEnabled === undefined || participantsFetchEnabled === true) && tracksProp === undefined,
            refetchOnWindowFocus: false,
        }
    );
    const tracks = tracksProp !== undefined ? tracksProp : tracksFromQuery;

    // Estimated splits are no longer fetched on load; use the Test button in the Rules pane to preview splits.
    const { data: calculatedSplits, isLoading: isLoadingSplits } = useQuery(
        ['estimatedSplits', String(templateId), typicalCost],
        () => calculateTestSplits(String(templateId), typicalCost, csrfToken),
        {
            enabled: false, // Do not fetch on load; Test button in Rules pane opens modal that fetches test-splits
            refetchOnWindowFocus: false,
            staleTime: 30000,
        }
    );

    // Create a map of user_id -> estimated cost
    const estimatedCostsMap = useMemo(() => {
        if (!calculatedSplits || !calculatedSplits.final_splits) return {};
        return calculatedSplits.final_splits;
    }, [calculatedSplits]);

    // Filled tracks (one row per track; use track.id as key to avoid duplicate-key when same user appears on multiple tracks)
    const filledTracks = useMemo(() => {
        if (!tracks || !Array.isArray(tracks)) return [];
        return tracks.filter(t => t.user_id && t.user);
    }, [tracks]);
    // Vacant tracks (tracks with no user assigned)
    const vacantTracks = useMemo(() => {
        if (!tracks || !Array.isArray(tracks)) return [];
        return tracks.filter(track => !track.user_id || track.user_id === '');
    }, [tracks]);

    // Handle filling a vacancy
    const handleFillVacancy = async (trackId, userEmail, isEditing = false) => {
        if (!userEmail || !IsValidEmail(userEmail)) {
            alert('Please enter a valid email address');
            return;
        }

        try {
            // Use same source as participants list (tracks → users)
            const currentTracks = await queryClient.fetchQuery(
                ['billingTracks', String(templateId)],
                () => getTracks(String(templateId), csrfToken)
            );
            const currentParticipants = usersFromTracks(currentTracks);

            const existingParticipant = currentParticipants?.find(p => {
                return p.email?.toLowerCase().trim() === userEmail.toLowerCase().trim();
            });

            if (existingParticipant && existingParticipant.id) {
                // User exists - just assign to vacancy track (currentTracks already fetched above)
                const currentTrack = currentTracks?.find(t => t.user_id === existingParticipant.id);

                // Update vacancy track and old track in parallel
                await Promise.all([
                    updateTrack(String(templateId), trackId, existingParticipant.id, csrfToken),
                    currentTrack && currentTrack.id !== trackId 
                        ? updateTrack(String(templateId), currentTrack.id, null, csrfToken)
                        : Promise.resolve()
                ]);
            } else {
                // User doesn't exist - add them
                // Backend will automatically reuse the vacant track when updating the default rule
                const mutation = addTemplateParticipant(csrfToken);
                await mutation({ email: userEmail, templateId: templateId });

                // Refetch tracks (single source) and derive participants
                const updatedTracks = await queryClient.fetchQuery(
                    ['billingTracks', String(templateId)],
                    () => getTracks(String(templateId), csrfToken),
                    { staleTime: 0 }
                );
                const updatedParticipants = usersFromTracks(updatedTracks);

                const newParticipant = updatedParticipants?.find(p => 
                    p.email?.toLowerCase().trim() === userEmail.toLowerCase().trim()
                );

                if (newParticipant && newParticipant.id) {
                    // Check if vacant track was reused
                    const vacantTrack = updatedTracks?.find(t => t.id === trackId);
                    const newParticipantTrack = updatedTracks?.find(t => t.user_id === newParticipant.id);

                    // If backend created a new track instead of reusing vacant one, fix it
                    if (newParticipantTrack && newParticipantTrack.id !== trackId && (!vacantTrack || !vacantTrack.user_id)) {
                        // Reassign to vacant track and delete the auto-created one
                        await Promise.all([
                            updateTrack(String(templateId), trackId, newParticipant.id, csrfToken),
                            deleteTrack(String(templateId), newParticipantTrack.id, csrfToken)
                        ]);
                    }
                }
            }

            await Promise.all([
                queryClient.refetchQueries(['billingTracks', String(templateId)]),
                queryClient.refetchQueries(['billRules', String(templateId)])
            ]);
            
            // Invalidate estimated splits (will auto-refetch when needed)
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
        } catch (error) {
            console.error('Error filling vacancy:', error);
            alert('Error filling vacancy: ' + (error.response?.data?.error || error.message));
        }
    };

    // Fetch group members (excluding current bill participants) only when user opens a participant dropdown
    const [participantDropdownEverOpened, setParticipantDropdownEverOpened] = useState(false);
    const { data: groupMembers, isLoading: loadingGroupMembers } = useQuery(
        ['groupParticipantsExcludingBill', groupId, templateId],
        () => getGroupParticipantsExcludingBill(groupId, templateId, csrfToken),
        {
            enabled: !!groupId && !!templateId && !!csrfToken && templateId !== 'none' && participantDropdownEverOpened,
            staleTime: 0, // Always refetch when switching bills
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );

    // Fetch all group participants only when a participant dropdown is opened (not on template view load)
    const { data: allGroupParticipants } = useQuery(
        ['groupParticipants', groupId],
        () => getGroupParticipants(groupId, csrfToken),
        {
            enabled: !!groupId && !!csrfToken && participantDropdownEverOpened,
            staleTime: 0,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );

    // Ensure current user is included in dropdown options if they're not already there
    const enhancedGroupMembers = useMemo(() => {
        if (loadingGroupMembers) return [];
        if (!groupMembers) return [];
        
        // Check if current user (by user_sub) is already in group members
        const currentUserInGroup = groupMembers.some(member => {
            const memberSub = typeof member === 'string' ? null : (member.user_sub || member.sub);
            return memberSub && authClaims?.user_sub && memberSub.toUpperCase() === authClaims.user_sub.toUpperCase();
        });

        // If current user is not in excluded group members, check if they're in all group participants
        if (!currentUserInGroup && authClaims?.user_sub && allGroupParticipants) {
            const currentUserInAllParticipants = allGroupParticipants.find(member => {
                const memberSub = typeof member === 'string' ? null : (member.user_sub || member.sub);
                return memberSub && memberSub.toUpperCase() === authClaims.user_sub.toUpperCase();
            });

            if (currentUserInAllParticipants) {
                const currentUserEmail = typeof currentUserInAllParticipants === 'string' 
                    ? currentUserInAllParticipants 
                    : currentUserInAllParticipants.email;
                
                if (currentUserEmail) {
                    // Check if current user's email is already in group members (by email match)
                    const emailInGroup = groupMembers.some(member => {
                        const memberEmail = typeof member === 'string' ? member : member.email;
                        return memberEmail && memberEmail.toLowerCase() === currentUserEmail.toLowerCase();
                    });
                    
                    if (!emailInGroup) {
                        // Add current user to group members
                        return [...groupMembers, typeof currentUserInAllParticipants === 'string' 
                            ? currentUserInAllParticipants 
                            : { ...currentUserInAllParticipants, email: currentUserEmail }];
                    }
                }
            }
        }

        // Fallback: If current user is the owner and they're already a participant, try to get their email
        if (!currentUserInGroup && authClaims?.user_sub && ownerSub) {
            const isCurrentUserOwner = authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();
            if (isCurrentUserOwner && participants) {
                const ownerParticipant = participants.find(p => {
                    const pSub = p.user_sub || p.sub;
                    return pSub && pSub.toUpperCase() === ownerSub.toUpperCase();
                });
                
                if (ownerParticipant && ownerParticipant.email) {
                    // Check if owner's email is already in group members
                    const emailInGroup = groupMembers.some(member => {
                        const memberEmail = typeof member === 'string' ? member : member.email;
                        return memberEmail && ownerParticipant.email && 
                               memberEmail.toLowerCase() === ownerParticipant.email.toLowerCase();
                    });
                    
                    if (!emailInGroup) {
                        // Add current user to group members
                        return [...groupMembers, { email: ownerParticipant.email, user_sub: ownerSub }];
                    }
                }
            }
        }
        
        return groupMembers;
    }, [groupMembers, loadingGroupMembers, authClaims?.user_sub, ownerSub, participants, allGroupParticipants]);

    // Reset and refetch when bill changes
    useEffect(() => {
        if (groupId && templateId && csrfToken) {
            queryClient.invalidateQueries(['groupParticipantsExcludingBill', groupId, templateId]);
        }
    }, [templateId, groupId, csrfToken, queryClient]);

    const { canEdit, toggleFormMode } = UseHybridForm();

    const handleParticipantRemoved = () => {
        if (onParticipantRemoved) {
            onParticipantRemoved();
        }
    };

    const isOwner = authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 'none',
                    borderBottom: 'none',
                    mb: expandedView ? 0 : '40px',
                    '& .MuiTableContainer-root': {
                        borderBottom: 'none !important',
                        '&::after': {
                            display: 'none !important',
                        },
                    },
                    ...props,
                }}
            >
                {/* Participants List with Estimated Costs - Table Format */}
                {templateId && templateId !== 'none' ? (
                    <TableContainer
                        component={Box}
                        sx={{
                            mb: 0,
                            pb: 0,
                            mt: 0,
                            pt: 0,
                            mr: 0,
                            ml: 0,
                            width: '100%',
                            borderRadius: expandedView ? 0 : 2,
                            backgroundColor: expandedView ? 'transparent' : (theme.colors.tableBackground || 'white'),
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: expandedView ? 'none' : (theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)'),
                            outline: 'none !important',
                            overflow: 'hidden',
                            overflowX: 'hidden',
                            '&::before': { display: 'none !important' },
                            '&::after': { display: 'none !important' },
                            '& .MuiTable-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                borderTop: 'none !important',
                                marginBottom: '0 !important',
                                '& .MuiTableBody-root': {
                                    border: 'none !important',
                                    borderBottom: 'none !important',
                                    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                        borderBottom: 'none !important',
                                    },
                                },
                            },
                        }}
                    >
                        <Table sx={{
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderCollapse: 'collapse',
                            borderSpacing: 0,
                            width: '100%',
                            tableLayout: 'auto', // Auto layout for flexible column sizing
                        }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '3.5rem', minWidth: '3.5rem', maxWidth: '3.5rem', p: 0, verticalAlign: 'middle' }}>
                                    {headerAction ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                            {headerAction}
                                        </Box>
                                    ) : null}
                                </TableCell>
                                <TableCell sx={{ 
                                    color: theme.colors.brandSecondary, 
                                    fontWeight: 700, 
                                    pl: 0, 
                                    fontSize: { xs: '1.5rem', sm: '1.75rem' }, 
                                    py: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 0, // Allows the cell to shrink - same as RulesList pattern
                                }}>Participants</TableCell>
                                <TableCell align="right" sx={{ 
                                    color: theme.colors.text, 
                                    fontWeight: 600, 
                                    padding: { xs: '1rem 0.125rem', sm: '1rem 0' },
                                    whiteSpace: 'nowrap',
                                    width: { xs: '3.125rem', sm: '6.25rem' }, // Convert 50px/100px to rem (same as RulesList)
                                    minWidth: { xs: '3.125rem', sm: '6.25rem' },
                                    maxWidth: { xs: '3.125rem', sm: '6.25rem' },
                                }}>Estimate</TableCell>
                                <TableCell sx={{ 
                                    width: '3.5rem !important', 
                                    minWidth: '3.5rem !important', 
                                    maxWidth: '3.5rem !important', 
                                    p: 0,
                                    flexShrink: 0,
                                    flexGrow: 0,
                                }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* While hydrating: one empty row, no add-user row */}
                            {hydrating ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <CircularProgress sx={{ color: theme.colors?.primary || theme.colors?.brandSecondary }} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                            {/* Regular Participants: one row per filled track; key includes index so duplicate track ids don't collide */}
                            {filledTracks.map((track, index) => {
                                const user = track.user;
                                const userEstimatedCost = user?.id ? (estimatedCostsMap[user.id] || null) : null;
                                return (
                                    <ParticipantItem
                                        key={`participant-${track.id ?? index}-${index}`}
                                        templateId={templateId}
                                        user={user}
                                        onRemove={handleParticipantRemoved}
                                        estimatedCost={userEstimatedCost}
                                        typicalCost={typicalCost}
                                        calculatedSplits={calculatedSplits}
                                        tracks={tracks}
                                        groupId={groupId}
                                        groupMembers={groupMembers}
                                        enhancedGroupMembers={enhancedGroupMembers || []}
                                        loadingGroupMembers={loadingGroupMembers}
                                        onEditParticipant={handleParticipantRemoved}
                                        editingUserId={editingUserId}
                                        setEditingUserId={setEditingUserId}
                                        isAddingUser={isAddingUser}
                                        setIsAddingUser={setIsAddingUser}
                                        onShowNotification={setNotification}
                                        onDropdownOpen={() => setParticipantDropdownEverOpened(true)}
                                    />
                                );
                            })}

                            {/* Vacant Tracks */}
                            {vacantTracks && vacantTracks.map((track, index) => (
                                <VacancyRow
                                    key={`vacancy-${track.id ?? index}-${index}`}
                                    track={track}
                                    templateId={templateId}
                                    isOwner={isOwner}
                                    onFillVacancy={handleFillVacancy}
                                    groupId={groupId}
                                    groupMembers={groupMembers}
                                    enhancedGroupMembers={enhancedGroupMembers || []}
                                    loadingGroupMembers={loadingGroupMembers}
                                    theme={theme}
                                    csrfToken={csrfToken}
                                    queryClient={queryClient}
                                    editingUserId={editingUserId}
                                    setEditingUserId={setEditingUserId}
                                    isAddingUser={isAddingUser}
                                    setIsAddingUser={setIsAddingUser}
                                    onShowNotification={setNotification}
                                    onDropdownOpen={() => setParticipantDropdownEverOpened(true)}
                                />
                            ))}

                            {/* Add Participant Row - hidden while hydrating */}
                            <AddParticipantRow
                                key="add-participant-row"
                                templateId={templateId}
                                groupId={groupId}
                                participants={participants}
                                onSubmit={onSubmit}
                                isOwner={isOwner}
                                isAddingUser={isAddingUser}
                                setIsAddingUser={setIsAddingUser}
                                editingUserId={editingUserId}
                                setEditingUserId={setEditingUserId}
                            />
                                </>
                            )}
                        </TableBody>
                    </Table>
                    </TableContainer>
                ) : null}

            </Box>
            
            {/* Notification Snackbar - at parent level so it persists */}
            <Snackbar
                open={notification.open}
                autoHideDuration={3000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setNotification({ ...notification, open: false })} 
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default FormParticipants;
